import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { 
  Users, 
  Search, 
  Plus, 
  Pencil, 
  Trash2,
  X,
  Building2,
  ArrowUpAZ,
  ArrowDownAZ
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { formatCurrency } from '../lib/utils';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const RECURRENCES = ['Mensuelle', 'Trimestrielle', 'Semestrielle', 'Annuelle', 'Ponctuelle'];

const emptyClient = {
  company_name: '',
  owner_name: '',
  address_line1: '',
  address_line2: '',
  postal_code: '',
  city: '',
  country: 'Suisse',
  phone: '',
  email: '',
  show_on_invoice: true,
  prestations: []
};

const emptyPrestation = {
  name: '',
  amount_ht: 0,
  recurrence: 'Mensuelle'
};

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' = A-Z, 'desc' = Z-A
  
  // Modal states
  const [showForm, setShowForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [editMode, setEditMode] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState(emptyClient);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/clients`);
      setClients(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  };

  // Filtered and sorted clients
  const filteredClients = useMemo(() => {
    let result = clients.filter(client => {
      if (!searchQuery) return true;
      const searchLower = searchQuery.toLowerCase();
      return (
        client.company_name.toLowerCase().includes(searchLower) ||
        client.owner_name.toLowerCase().includes(searchLower)
      );
    });
    
    // Sort alphabetically
    result.sort((a, b) => {
      const nameA = a.company_name.toLowerCase();
      const nameB = b.company_name.toLowerCase();
      if (sortOrder === 'asc') {
        return nameA.localeCompare(nameB, 'fr');
      } else {
        return nameB.localeCompare(nameA, 'fr');
      }
    });
    
    return result;
  }, [clients, searchQuery, sortOrder]);

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode && selectedClient) {
        await axios.put(`${API_URL}/clients/${selectedClient.id}`, formData);
        toast.success('Client mis à jour');
      } else {
        await axios.post(`${API_URL}/clients`, formData);
        toast.success('Client créé');
      }
      setShowForm(false);
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async () => {
    if (!selectedClient) return;
    try {
      await axios.delete(`${API_URL}/clients/${selectedClient.id}`);
      toast.success('Client supprimé');
      setShowDeleteConfirm(false);
      setSelectedClient(null);
      fetchClients();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData(emptyClient);
    setEditMode(false);
    setSelectedClient(null);
  };

  const openEdit = (client) => {
    setSelectedClient(client);
    setFormData({
      company_name: client.company_name,
      owner_name: client.owner_name,
      address_line1: client.address_line1 || '',
      address_line2: client.address_line2 || '',
      postal_code: client.postal_code || '',
      city: client.city || '',
      country: client.country || 'Suisse',
      phone: client.phone || '',
      email: client.email || '',
      show_on_invoice: client.show_on_invoice ?? true,
      prestations: client.prestations || []
    });
    setEditMode(true);
    setShowForm(true);
  };

  const openDelete = (client) => {
    setSelectedClient(client);
    setShowDeleteConfirm(true);
  };

  const addPrestation = () => {
    setFormData({
      ...formData,
      prestations: [...formData.prestations, { ...emptyPrestation, id: Date.now().toString() }]
    });
  };

  const updatePrestation = (index, field, value) => {
    const updated = [...formData.prestations];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, prestations: updated });
  };

  const removePrestation = (index) => {
    const updated = formData.prestations.filter((_, i) => i !== index);
    setFormData({ ...formData, prestations: updated });
  };

  const getActivePrestationsCount = (client) => {
    return client.prestations?.length || 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      <Toaster position="top-right" />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Clients</h1>
        <Button 
          onClick={() => { resetForm(); setShowForm(true); }}
          className="rounded-lg"
          data-testid="new-client-button"
        >
          <Plus size={18} className="mr-2" />
          Nouveau client
        </Button>
      </div>

      {/* Search and Sort */}
      <div className="flex gap-4 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <Input
            type="text"
            placeholder="Rechercher par nom d'entreprise ou propriétaire..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-11 h-11 border-slate-200"
            data-testid="search-clients-input"
          />
        </div>
        <Button
          variant="outline"
          onClick={toggleSortOrder}
          className="rounded-lg flex items-center gap-2"
          data-testid="sort-clients-button"
        >
          {sortOrder === 'asc' ? (
            <>
              <ArrowUpAZ size={18} />
              A-Z
            </>
          ) : (
            <>
              <ArrowDownAZ size={18} />
              Z-A
            </>
          )}
        </Button>
      </div>

      {/* Clients list */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {filteredClients.length === 0 ? (
          <div className="empty-state">
            <Building2 size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-lg font-medium">Aucun client trouvé</p>
            <p className="text-sm">Créez votre premier client pour commencer</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table responsive-table" data-testid="clients-table">
              <thead>
                <tr>
                  <th>Entreprise</th>
                  <th>Responsable</th>
                  <th>Ville</th>
                  <th>Prestations</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((client) => (
                  <tr key={client.id} data-testid={`client-row-${client.id}`}>
                    <td data-label="Entreprise" className="font-medium">{client.company_name}</td>
                    <td data-label="Responsable">{client.owner_name}</td>
                    <td data-label="Ville">{client.city || '-'}</td>
                    <td data-label="Prestations">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 text-blue-600 text-sm font-medium">
                        {getActivePrestationsCount(client)}
                      </span>
                    </td>
                    <td data-label="Actions">
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(client)}
                          className="rounded-lg"
                          data-testid={`edit-client-${client.id}`}
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDelete(client)}
                          className="rounded-lg text-red-500 hover:text-red-600 hover:bg-red-50"
                          data-testid={`delete-client-${client.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Client Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="client-form-dialog">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Modifier le client' : 'Nouveau client'}</DialogTitle>
            <DialogDescription>
              {editMode ? 'Modifiez les informations du client' : 'Remplissez les informations du nouveau client'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* General info */}
            <div className="form-section">
              <h3 className="form-section-title">Informations générales</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom de l'entreprise *</Label>
                  <Input
                    value={formData.company_name}
                    onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                    required
                    data-testid="form-company-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nom du propriétaire / responsable *</Label>
                  <Input
                    value={formData.owner_name}
                    onChange={(e) => setFormData({...formData, owner_name: e.target.value})}
                    required
                    data-testid="form-owner-name"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label>Adresse ligne 1</Label>
                  <Input
                    value={formData.address_line1}
                    onChange={(e) => setFormData({...formData, address_line1: e.target.value})}
                    data-testid="form-address1"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Adresse ligne 2</Label>
                  <Input
                    value={formData.address_line2}
                    onChange={(e) => setFormData({...formData, address_line2: e.target.value})}
                    data-testid="form-address2"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="space-y-2">
                  <Label>Code postal</Label>
                  <Input
                    value={formData.postal_code}
                    onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                    data-testid="form-postal-code"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ville</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    data-testid="form-city"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Pays</Label>
                  <Input
                    value={formData.country}
                    onChange={(e) => setFormData({...formData, country: e.target.value})}
                    data-testid="form-country"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label>Téléphone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    data-testid="form-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    data-testid="form-email"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2 mt-4">
                <Checkbox
                  id="show_on_invoice"
                  checked={formData.show_on_invoice}
                  onCheckedChange={(checked) => setFormData({...formData, show_on_invoice: checked})}
                  data-testid="form-show-on-invoice"
                />
                <Label htmlFor="show_on_invoice" className="cursor-pointer">
                  Afficher sur la facture
                </Label>
              </div>
            </div>

            {/* Prestations */}
            <div className="form-section">
              <div className="flex items-center justify-between mb-4">
                <h3 className="form-section-title mb-0">Prestations du client</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addPrestation}
                  className="rounded-lg"
                  data-testid="add-prestation-button"
                >
                  <Plus size={16} className="mr-1" />
                  Ajouter une prestation
                </Button>
              </div>
              
              {formData.prestations.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">
                  Aucune prestation définie
                </p>
              ) : (
                <div className="space-y-3">
                  {formData.prestations.map((prestation, index) => (
                    <div key={prestation.id || index} className="flex gap-3 items-start bg-white p-3 rounded-lg border" data-testid={`prestation-row-${index}`}>
                      <div className="flex-1">
                        <Input
                          placeholder="Nom de la prestation"
                          value={prestation.name}
                          onChange={(e) => updatePrestation(index, 'name', e.target.value)}
                          className="mb-2"
                          data-testid={`prestation-name-${index}`}
                        />
                        <div className="flex gap-2">
                          <Input
                            type="number"
                            placeholder="Montant HT"
                            value={prestation.amount_ht}
                            onChange={(e) => updatePrestation(index, 'amount_ht', parseFloat(e.target.value) || 0)}
                            className="w-32"
                            data-testid={`prestation-amount-${index}`}
                          />
                          <span className="flex items-center text-sm text-slate-500">CHF</span>
                          <Select 
                            value={prestation.recurrence} 
                            onValueChange={(v) => updatePrestation(index, 'recurrence', v)}
                          >
                            <SelectTrigger className="w-40" data-testid={`prestation-recurrence-${index}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {RECURRENCES.map(r => (
                                <SelectItem key={r} value={r}>{r}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removePrestation(index)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        data-testid={`remove-prestation-${index}`}
                      >
                        <X size={16} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="rounded-lg">
                Annuler
              </Button>
              <Button type="submit" className="rounded-lg" data-testid="form-submit">
                {editMode ? 'Enregistrer' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent data-testid="delete-client-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer le client "{selectedClient?.company_name}" ? 
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="rounded-lg bg-destructive" data-testid="confirm-delete-client">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Clients;
